"use client";

import { c as _c } from "react/compiler-runtime";
import { Article, Calculator, Page } from "@/sanity/sanity.types";
import Card, { CardStyle } from "../common/card/card";
import GridColumn from "../common/grid-column/grid-column";
import GridRow from "../common/grid-row/grid-row";
import { usePerformance } from "@/contexts/performance-context";
import { useEffect, useState } from "react";
import Image from "@/components/common/image/image";
import Link, { LinkStyle, LinkType } from "../common/link/link";
import CalculatorFooter from "./calculator-footer";
import ArrowIcon from "../common/arrow-icon/arrow-icon";
import Button, { ButtonStyle } from "../common/button/button";
import imageUrl from "@/sanity/utilities/imageUrl";
import { getTrustPilotRating } from "@/services/wizard-service";
type ArticleWithCalculator = Article & {
  calculator?: Calculator[];
};
interface ICalculatorProps {
  data: ArticleWithCalculator[];
  beforeQueryTime: number;
  afterQueryTime: number;
  layout?: Page;
}
interface TrustPilotData {
  rating: number;
  reviews: number;
}
const Calculators = props => {
  const $ = _c(36);
  const {
    data,
    beforeQueryTime,
    afterQueryTime,
    layout
  } = props;
  const {
    checkTimeElapsed
  } = usePerformance();
  const [open, setOpen] = useState(false);
  const [trustPilot, setTrustPilot] = useState(null);
  let t0;
  if ($[0] !== afterQueryTime || $[1] !== beforeQueryTime || $[2] !== checkTimeElapsed) {
    t0 = () => {
      checkTimeElapsed(beforeQueryTime, "Calculator Page before query started");
      checkTimeElapsed(afterQueryTime, "Calculator page after query completed");
    };
    $[0] = afterQueryTime;
    $[1] = beforeQueryTime;
    $[2] = checkTimeElapsed;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
  const cards = data.flatMap(_temp).filter(Boolean);
  const categories = Array.from(new Set(cards.map(_temp2).filter(_temp3)));
  const tabs = ["All", ...categories] as const;
  const [activeTab, setActiveTab] = useState("All");
  const redirectMap = {
    "buy-a-home": "https://apply.amerisave.com/quote/v1/purchase/estimated-price",
    refinance: "https://apply.amerisave.com/quote/v1/refi-goals"
  };
  const modules = layout?.modules || [];
  const heroOptionCalculator = modules?.filter(_temp4);
  let t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const fetchData = async () => {
        const data_0 = await getTrustPilotRating();
        setTrustPilot(data_0);
      };
      fetchData();
    };
    t3 = [];
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const filteredCards = activeTab === "All" ? cards : cards.filter(item_0 => item_0?.category === activeTab);
  const formatLabel = _temp6;
  const getRedirectUrl = () => {
    if (activeTab === "All") {
      return "http://apply.amerisave.com";
    }
    return redirectMap[activeTab] || "http://apply.amerisave.com";
  };
  const t4 = `${trustPilot?.rating} out of 5 stars`;
  let t5;
  if ($[7] !== t4) {
    t5 = <Image src="/stars.svg" alt={t4} width={90} height={18} />;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="text-center font-['Work_Sans'] text-sm font-bold uppercase leading-6 tracking-[1.12px] text-[#009490]">TRUSTED BY MILLIONS</span>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <div className="inline-flex items-center gap-2.5 rounded-xl bg-[#00949014] px-4 py-1">{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = layout?.title;
  let t9;
  if ($[12] !== t8) {
    t9 = <h1 className="my-[12px] text-center font-lora text-[24px] font-semibold tracking-[-1px] text-[#252C32] md:text-[28px] md:leading-[36px] lg:my-[20px] lg:text-[40px] lg:leading-[44px]">{t8}</h1>;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <p className="text-base font-normal md:leading-[22px] lg:text-[20px] lg:leading-[26px]">Crunch the numbers. See your estimates. Achieve your financial goals.</p>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t7 || $[16] !== t9) {
    t11 = <GridRow><GridColumn><div className="m-auto flex max-w-3xl flex-col items-center gap-1 text-center">{t7}{t9}{t10}</div></GridColumn></GridRow>;
    $[15] = t7;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== open) {
    t12 = () => setOpen(!open);
    $[18] = open;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== activeTab) {
    t13 = activeTab === "All" ? activeTab : formatLabel(activeTab);
    $[20] = activeTab;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = `transition-transform ${open ? "rotate-180" : ""}`;
  let t15;
  if ($[22] !== t14) {
    t15 = <Image src="/caret-down-mobile.svg" alt="Dropdown Icon" className={t14} width={20} />;
    $[22] = t14;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t12 || $[25] !== t13 || $[26] !== t15) {
    t16 = <button onClick={t12} className="flex w-full items-center  justify-between rounded-md border border-gray-300 bg-white px-4 py-3 text-[16px] font-semibold text-[#373A36]">{t13}{t15}</button>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const t17 = open && <div className="absolute left-0 z-10 mt-2 w-full overflow-hidden rounded-md border bg-white text-[16px] font-semibold text-[#373A36] shadow-lg">{tabs.map(tab => <div key={tab} onClick={() => {
      setActiveTab(tab);
      setOpen(false);
    }} className={`cursor-pointer px-4 py-3 text-sm ${activeTab === tab ? "bg-[#cce5ef]" : "hover:bg-gray-100"}`}>{tab === "All" ? tab : formatLabel(tab)}</div>)}</div>;
  let t18;
  if ($[28] !== t16 || $[29] !== t17) {
    t18 = <div className="relative block w-full px-4 md:hidden">{t16}{t17}</div>;
    $[28] = t16;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== heroOptionCalculator?.[0] || $[32] !== props.layout) {
    t19 = props.layout && <CalculatorFooter data={heroOptionCalculator?.[0]} />;
    $[31] = heroOptionCalculator?.[0];
    $[32] = props.layout;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t19) {
    t20 = <div className="w-full pt-[56px]">{t19}</div>;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  return <div className="flex flex-col items-center justify-center pb-[64px] pt-[47px]">{t11}<GridRow><GridColumn className="py-[40px] lg:pb-[44px] lg:pt-[64px]">{t18}<div className="mx-auto hidden w-fit flex-wrap content-start items-start gap-[12px] rounded-md bg-[#4a4a4a0f] p-[6px] md:flex">{tabs.map(tab_0 => <Button key={tab_0} style={ButtonStyle.Secondary} onClick={() => setActiveTab(tab_0)} className={`rounded-lg px-6 py-2 !font-work-sans text-[16px] h-[48px] font-medium leading-[24px] !text-[#373B37] transition-all ${activeTab === tab_0 ? "rounded-md border-4 !border-[#00698F] !bg-[#F2F7F9]" : "!text-[#373B37] !rounded-md !bg-white !shadow-[0_1px_4px_rgba(226,226,226,0.24)] !border-none"}`}>{tab_0 === "All" ? tab_0 : formatLabel(tab_0)}</Button>)}</div></GridColumn></GridRow>{data && <GridRow><GridColumn><div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"><div className="h-full md:row-span-2"><div className="relative flex h-full min-h-[508px] flex-col gap-4 rounded-[12px] bg-[#F4F6F8] p-6"><div><h2 className="font-lora text-[32px] font-semibold tracking-[0.64px] text-[#373B37] ">Calculate your real<br />loan options—quickly.</h2><p className="font-workSans mt-2 text-base font-medium leading-6 text-[#373B37]">Answer a few questions and we'll find the best loan for your goals.</p></div><Link href={getRedirectUrl()} className="font-workSans mt-auto flex items-center !text-base font-semibold !leading-5 !tracking-[-0.08px] text-[#373B37]">Start Calculating<ArrowIcon className="ml-2 inline-block align-middle" /></Link><div className="absolute bottom-0 right-0"><Image src={heroOptionCalculator[0]?.background?.asset ? imageUrl(heroOptionCalculator[0]?.background?.asset).url() : ""} alt=" image" className="w-[220px] object-contain" width={220} height={220} /></div></div></div>{filteredCards.map(_temp7)}</div></GridColumn></GridRow>}{t20}</div>;
};
export default Calculators;
function _temp(article) {
  return (article.calculator ?? []).map(calc => ({
    ...calc,
    excerpt: article.excerpt,
    slug: article.slug,
    articleTitle: article.title
  }));
}
function _temp2(item) {
  return item.category;
}
function _temp3(cat) {
  return cat === "buy-a-home" || cat === "refinance";
}
function _temp4(module) {
  return module._type === "heroOptions";
}
function _temp5(c) {
  return c.toUpperCase();
}
function _temp6(value) {
  return typeof value === "string" ? value.replace(/-/g, " ").replace(/\b\w/g, _temp5) : "";
}
function _temp7(item_1, i) {
  return <Card title={item_1?.title ?? ""} description={item_1?.excerpt ?? ""} linkUrl={`/learn/${item_1?.slug}`} linkLabel="Calculate" style={CardStyle.Tertiary} key={`${item_1?.calculatorId} + ${i}`} />;
}